import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Briefcase, MapPin, Upload } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

export default function Careers() {
  const [jobs, setJobs] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", mobile: "", position: "", cover_letter: "" });
  const [resume, setResume] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { api.get("/careers").then((r) => setJobs(r.data)); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      let resume_path = "";
      if (resume) {
        const fd = new FormData(); fd.append("file", resume);
        const up = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        resume_path = up.data.storage_path;
      }
      await api.post("/career-applications", { ...form, resume_path });
      toast.success("Application submitted! Our HR team will reach out.");
      setForm({ name: "", email: "", mobile: "", position: "", cover_letter: "" }); setResume(null);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to submit");
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="careers-page">
      <section className="hero-bg py-12">
        <div className="container-x">
          <span className="font-cursive text-2xl text-brand-purple">Careers</span>
          <h1 className="text-4xl sm:text-5xl font-bold">Join the One Hospitals family</h1>
          <p className="text-slate-600 mt-3 max-w-2xl">Build a meaningful career with one of South India's most respected healthcare brands.</p>
        </div>
      </section>

      <section className="container-x py-10">
        <h2 className="text-2xl font-bold mb-6">Current Openings</h2>
        <div className="grid md:grid-cols-2 gap-5">
          {jobs.map((j) => (
            <div key={j.id} className="card-clean p-6" data-testid={`job-${j.id}`}>
              <div className="text-xs text-brand-green font-semibold uppercase">{j.department}</div>
              <div className="font-bold text-lg mt-1">{j.title}</div>
              <div className="flex items-center gap-3 text-xs text-slate-500 mt-1"><span className="flex items-center gap-1"><Briefcase className="h-3.5 w-3.5" />{j.type}</span><span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{j.location}</span></div>
              <p className="text-sm text-slate-600 mt-3">{j.description}</p>
              <details className="mt-3 text-sm"><summary className="cursor-pointer text-brand-purple font-semibold">Requirements</summary><ul className="list-disc pl-5 text-slate-600 mt-2">{j.requirements?.map((r, i) => <li key={i}>{r}</li>)}</ul></details>
              <button onClick={() => setForm((f) => ({ ...f, position: j.title }))} className="btn-outline mt-4 text-sm" data-testid={`apply-${j.id}`}>Apply for this role</button>
            </div>
          ))}
        </div>
      </section>

      <section className="container-x pb-16">
        <div className="card-clean p-6 md:p-10 max-w-3xl mx-auto" data-testid="career-application-form">
          <h2 className="text-2xl font-bold">Apply Now</h2>
          <p className="text-sm text-slate-500 mt-1">Upload your resume and we'll get in touch.</p>
          <form onSubmit={submit} className="grid md:grid-cols-2 gap-4 mt-5">
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" data-testid="career-name" />
            <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" data-testid="career-email" />
            <input required value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} placeholder="Mobile number" data-testid="career-mobile" />
            <input required value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} placeholder="Position applying for" data-testid="career-position" />
            <textarea rows="4" className="md:col-span-2" value={form.cover_letter} onChange={(e) => setForm({ ...form, cover_letter: e.target.value })} placeholder="Cover letter (optional)" data-testid="career-cover" />
            <label className="md:col-span-2 flex items-center gap-3 border border-dashed border-slate-300 rounded-xl p-4 bg-slate-50 cursor-pointer">
              <Upload className="h-5 w-5 text-brand-purple" />
              <span className="text-sm text-slate-600">{resume ? resume.name : "Click to upload resume (PDF, DOC, max 10MB)"}</span>
              <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={(e) => setResume(e.target.files?.[0] || null)} data-testid="career-resume" />
            </label>
            <button disabled={submitting} className="btn-primary md:col-span-2 justify-center" data-testid="career-submit">{submitting ? "Submitting…" : "Submit Application"}</button>
          </form>
        </div>
      </section>
    </div>
  );
}
